import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { endpoints } from '../api/endpoints';
import type { ApiKey } from '../api/types';
import { applyTheme, useAuth } from '../auth/AuthContext';
import { ErrorBox, formatDate, Notice, useAsync } from '../components/common';
import i18n, { normalizeLanguage, SUPPORTED_LANGUAGES } from '../i18n';

export function ProfilePage() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [language, setLanguage] = useState(normalizeLanguage(user?.preferences?.language ?? i18n.language));
  const [theme, setTheme] = useState(user?.preferences?.theme ?? 'dark');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      const updated = await endpoints.users.updateMe({
        full_name: fullName,
        preferences: { ...(user?.preferences ?? {}), language, theme },
      });
      setUser(updated);
      applyTheme(theme);
      await i18n.changeLanguage(language);
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div>
      <h1>{t('profile.title')}</h1>
      <form className="card form" onSubmit={save}>
        <label>
          {t('auth.fullName')}
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>
        <label>
          {t('auth.email')}
          <input value={user?.email ?? ''} readOnly />
        </label>
        <label>
          {t('lang.label')}
          <select value={language} onChange={(e) => setLanguage(normalizeLanguage(e.target.value))}>
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {t(`lang.${l}`)}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend>{t('profile.theme')}</legend>
          <label>
            <input type="radio" name="theme" checked={theme === 'dark'} onChange={() => setTheme('dark')} />
            {t('profile.themeDark')}
          </label>
          <label>
            <input type="radio" name="theme" checked={theme === 'light'} onChange={() => setTheme('light')} />
            {t('profile.themeLight')}
          </label>
        </fieldset>
        <ErrorBox error={error} />
        {saved && <Notice>{t('profile.saved')}</Notice>}
        <button type="submit">{t('profile.save')}</button>
      </form>
      <PasswordForm />
      <ApiKeysSection />
    </div>
  );
}

function PasswordForm() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    try {
      await endpoints.users.changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
    } catch (err) {
      setError(err);
    }
  };

  return (
    <form className="card form" onSubmit={submit}>
      <h2>{t('profile.changePassword')}</h2>
      <label>
        {t('profile.currentPassword')}
        <input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label>
        {t('profile.newPassword')}
        <input type="password" autoComplete="new-password" minLength={8} required value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      <ErrorBox error={error} />
      {done && <Notice>{t('profile.passwordChanged')}</Notice>}
      <button type="submit">{t('common.save')}</button>
    </form>
  );
}

function ApiKeysSection() {
  const { t, i18n: i } = useTranslation();
  const { data, error, reload } = useAsync(() => endpoints.apiKeys.list(), []);
  const [name, setName] = useState('');
  const [created, setCreated] = useState<ApiKey | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setCreated(await endpoints.apiKeys.create({ name }));
      setName('');
      await reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <section className="card">
      <h2>{t('profile.apiKeys')}</h2>
      <ErrorBox error={error || actionError} onRetry={reload} />
      {created?.key && (
        <Notice>
          {t('profile.keyCreated')} <code>{created.key}</code>
        </Notice>
      )}
      <form className="inline-form" onSubmit={create}>
        <label>
          {t('profile.apiKeyName')}
          <input required value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <button type="submit">{t('profile.createKey')}</button>
      </form>
      <table>
        <thead>
          <tr>
            <th scope="col">{t('profile.apiKeyName')}</th>
            <th scope="col">{t('profile.expires')}</th>
            <th scope="col">{t('admin.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((k) => (
            <tr key={k.id}>
              <td>{k.name}</td>
              <td>{formatDate(k.expires_at, i.language)}</td>
              <td>
                {k.revoked ? (
                  t('profile.revoked')
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await endpoints.apiKeys.revoke(k.id);
                        await reload();
                      } catch (err) {
                        setActionError(err);
                      }
                    }}
                  >
                    {t('profile.revoke')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
